<template>
  <div class="alumni-password-reset">
    <div class="inner">
      <template v-if="passwordResetStatus !== 'success'">
        <p class="description">{{ t('description') }}</p>
        <form class="form" @submit.prevent="resetPassword">
          <FormInput
            v-model="credentials.email"
            type="email"
            name="email"
            autocomplete="username"
            :label="t('email')"
            :error="t('error.email')"
            required
          />
          <button class="button-secondary" type="submit">{{ t('resetPassword') }}</button>
        </form>
      </template>
      <div v-else-if="passwordResetStatus === 'success'" class="confirmation">
        <h3 class="title text-2xl">{{ t('confirmation.title') }}</h3>
        <p>{{ t('confirmation.text') }}</p>
      </div>
    </div>
  </div>
</template>

<i18n lang="yaml">
de:
  email: E-Mail
  resetPassword: Passwort zurücksetzen
  description: Eine E-Mail mit Anweisungen zum Zurücksetzen Ihres Passworts wird an die angegebene E-Mail-Adresse gesendet.
  confirmation:
    title: Passwort zurücksetzen erfolgreich
    text: Wenn ein Konto mit dieser E-Mail-Adresse verknüpft ist, erhalten Sie eine E-Mail mit Anweisungen, wie Sie Ihr Passwort zurücksetzen können.
  error:
    email: Bitte geben Sie eine gültige E-Mail-Adresse ein.
en:
  email: Email
  resetPassword: Reset password
  description: An email with instructions on how to reset your password will be sent to the specified email address.
  confirmation:
    title: Password reset successful
    text: If an account is associated with this email, you will receive an email with instructions on how to reset your password
  error:
    email: Please enter a valid email address.
</i18n>

<script setup lang="ts">
const { t } = useI18n();

const credentials = reactive({
  email: '',
});

const passwordResetStatus = ref<'idle' | 'pending' | 'success' | 'error'>('idle');

async function resetPassword() {
  passwordResetStatus.value = 'pending';

  try {
    await $fetch('/api/alumni/reset-password', {
      method: 'POST',
      body: credentials,
    });

    passwordResetStatus.value = 'success';
  } catch (error) {
    console.error(error);
    passwordResetStatus.value = 'error';
  }
}
</script>

<style scoped lang="scss">
.alumni-password-reset {
  @include grid-layout();

  & {
    padding: var(--site-inner-padding);
    width: 100%;
    max-width: var(--site-max-width);
  }

  > .inner {
    @media (--xs), (--sm) {
      padding: var(--spacing-sm) 0;
    }

    @media (--xs) {
      grid-column: 1 / -1;
    }

    @media (--sm) {
      grid-column: 2 / 24;
    }

    @media (--md), (--lg) {
      grid-column: 5 / 21;
      padding-bottom: var(--spacing-2xl);
    }

    > .form {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--spacing-xs);
      padding-top: var(--spacing-xs);

      > .form-input {
        width: 100%;
      }
    }

    > .confirmation {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--spacing-xs);
    }
  }
}
</style>
